import React from 'react';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import type {
  AdminChatTraceResponse,
  TraceRequest,
  TraceTimingBreakdown,
} from '../../api';
import {
  compactStatusLabel,
  formatCount,
  formatMs,
  shortId,
  statusVariant,
} from '../../format';
import {
  sortTraceRequests,
  type TracePresentation,
} from '../../trace/presentation';
import { RequestSummary, Metric } from './request-summary';

function traceDiagnostics(trace: AdminChatTraceResponse | null) {
  return Array.isArray(trace?.diagnostics) ? trace.diagnostics : [];
}

function buildCriticalPathSegments(timing?: TraceTimingBreakdown) {
  const queue = Math.max(timing?.queue_ms || 0, 0);
  const activation = Math.max(timing?.activation_ms || 0, 0);
  const llm = Math.max(timing?.llm_ms || 0, 0);
  const tool = Math.max(timing?.tool_wall_ms || 0, 0);
  const persist = Math.max(timing?.persist_ms || 0, 0);

  return [
    { label: 'Queue', duration: queue, className: 'bg-slate-400' },
    {
      label: 'Activation wall',
      duration: activation,
      className: 'bg-zinc-500',
    },
    { label: 'LLM wall', duration: llm, className: 'bg-blue-600' },
    {
      label: 'Tool wall',
      duration: tool,
      className: 'bg-amber-500',
    },
    {
      label: 'Persist',
      duration: persist,
      className: 'bg-emerald-600',
    },
  ];
}

export function PerformanceTab({
  trace,
  request,
  selectedRequestID,
  onRequestSelect,
  presentation,
  onNavigate,
}: {
  trace: AdminChatTraceResponse | null;
  request: TraceRequest | null;
  selectedRequestID: string;
  onRequestSelect: (requestID: string) => void;
  presentation: TracePresentation | null;
  onNavigate: (eventID: string) => void;
}) {
  const timing = request?.timing || trace?.performance.timing;
  const criticalPath = buildCriticalPathSegments(timing);
  const scaleMS = Math.max(
    timing?.total_ms || 0,
    ...criticalPath.map(segment => segment.duration),
    1
  );
  const toolRows = trace?.performance.tool_cost_rows || [];
  const diagnostics = traceDiagnostics(trace);
  const navigateToTool = (row: { tool_name: string; status?: string }) => {
    const match = presentation?.navigationOrder.find(
      item =>
        item.event.kind === 'tool_call' &&
        String(item.event.details?.tool_name || item.event.title) ===
          row.tool_name &&
        (!row.status || item.event.status === row.status)
    );
    if (match) onNavigate(match.event.id);
  };
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Request</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {sortTraceRequests(trace?.requests || []).map(item => (
              <Button
                key={item.request_id}
                type="button"
                size="sm"
                variant={
                  item.request_id === selectedRequestID ? 'default' : 'outline'
                }
                onClick={() => onRequestSelect(item.request_id)}
              >
                {item.label || shortId(item.request_id)}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      <RequestSummary request={request} trace={trace} />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Critical Path</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Metric label="TTFT marker" value={formatMs(timing?.ttft_ms)} />
            <Metric label="LLM aggregate" value={formatMs(timing?.llm_ms)} />
            <Metric
              label="Tool aggregate"
              value={formatMs(timing?.tool_sum_ms)}
            />
            <Metric label="Wall time" value={formatMs(timing?.total_ms)} />
          </div>
          <div className="relative h-8 overflow-hidden rounded bg-muted">
            {
              criticalPath.reduce<{
                offset: number;
                nodes: React.ReactNode[];
              }>(
                (result, segment) => {
                  const width =
                    segment.duration > 0
                      ? Math.max(0.4, (segment.duration / scaleMS) * 100)
                      : 0;
                  result.nodes.push(
                    <div
                      key={segment.label}
                      className={`absolute top-0 h-8 ${segment.className}`}
                      title={`${segment.label}: ${formatMs(segment.duration)}`}
                      style={{
                        left: `${result.offset}%`,
                        width: `${Math.min(100 - result.offset, width)}%`,
                      }}
                    />
                  );
                  result.offset += width;
                  return result;
                },
                { offset: 0, nodes: [] }
              ).nodes
            }
            {timing?.ttft_ms && timing.total_ms ? (
              <div
                className="absolute top-0 h-8 border-l-2 border-foreground"
                title={`TTFT ${formatMs(timing.ttft_ms)}`}
                style={{
                  left: `${Math.min(100, (timing.ttft_ms / timing.total_ms) * 100)}%`,
                }}
              />
            ) : null}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {criticalPath.map(segment => (
              <span key={segment.label}>
                {segment.label} {formatMs(segment.duration)}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tool Cost</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Calls</TableHead>
                  <TableHead>Errors</TableHead>
                  <TableHead>Timeouts</TableHead>
                  <TableHead>Observed</TableHead>
                  <TableHead>Sum</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead>Retry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {toolRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="h-20 text-center text-muted-foreground"
                    >
                      No tool calls.
                    </TableCell>
                  </TableRow>
                ) : (
                  toolRows.map(row => (
                    <TableRow
                      key={row.tool_name}
                      className="cursor-pointer"
                      tabIndex={0}
                      title="Jump to timeline event"
                      onClick={() => navigateToTool(row)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          navigateToTool(row);
                        }
                      }}
                    >
                      <TableCell>{row.tool_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>
                          {compactStatusLabel(row.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCount(row.calls)}</TableCell>
                      <TableCell>{formatCount(row.errors)}</TableCell>
                      <TableCell>{formatCount(row.timeouts)}</TableCell>
                      <TableCell>{formatMs(row.observed_wall_ms)}</TableCell>
                      <TableCell>{formatMs(row.observed_sum_ms)}</TableCell>
                      <TableCell>
                        {formatCount(row.input_tokens + row.output_tokens)}
                      </TableCell>
                      <TableCell>{row.retry_quality}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bottlenecks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(trace?.performance.slow_spans || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No spans over 500 ms or error spans.
            </div>
          ) : (
            trace?.performance.slow_spans.map(span => (
              <button
                key={span.id}
                type="button"
                className="flex w-full items-center justify-between rounded-md border p-2 text-left text-sm hover:bg-muted"
                onClick={() => onNavigate(span.id)}
              >
                <span>{span.title}</span>
                <span className="text-muted-foreground">
                  {formatMs(span.duration_ms)}
                </span>
              </button>
            ))
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {diagnostics.length === 0 ? (
            <div className="text-sm text-muted-foreground">No diagnostics.</div>
          ) : (
            diagnostics.map(diagnostic => (
              <button
                key={diagnostic.id}
                type="button"
                className="w-full rounded-md border p-2 text-left text-sm hover:bg-muted"
                onClick={() => {
                  const eventID = diagnostic.affected_event_ids?.[0];
                  if (eventID) onNavigate(eventID);
                }}
              >
                <div className="font-medium">{diagnostic.title}</div>
                <div className="text-muted-foreground">
                  {diagnostic.detail || diagnostic.severity}
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
